import React from 'react';
import {  ScrollView, View } from 'react-native';
import { observer } from 'mobx-react';
import { useIsConnected } from '../../../../hooks/useIsConnected';
import { useStores } from '../../../../hooks/useStores';

import Error from '../../../../components/Error/Error';
import Loading from '../../../../components/Loading/Loading';
import Show from '../../../../components/Show/Show';
import { Banner } from '../../../../components/Banner/Banner';
import { Body1Reg, Title2 } from '../../../../config/designSystem/typography';
import { DesignSystemButtonTertiaryButton } from '../../../../components/Button/DesignSystemTertiaryButton';
import { DesignSystemTwoButtonsVertical } from '../../../../components/Button/DesignSystemTwoButtonsVertical';
import { EvolunoClose } from '../../../../assets/images/Icons/Close/EvolunoClose';
import { ModalChallenge } from '../../../../components/Challenge/ModalComplete';
import { SPACING } from '../../../../config/designSystem/Spacing';
import { concatPath } from '../../../../utils/links/concatPath';
import { dashboardKey } from '../../../../constants/routeKeys';
import { logAmplitude } from '../../../../utils/analytics/analytics';
import { router, useLocalSearchParams } from 'expo-router';
import { useFetchedContent } from '../../../../hooks/content/useFetchedContent';
import { useSafeAreaInsets } from 'react-native-safe-area-context';


const StepAction = observer(() => {
  // local, not global: every content screen reads an "id" param, and a global
  // read leaks the focused route's id into the screens still mounted behind it
  const params = useLocalSearchParams();
  const id = params.id;

  const rootStore = useStores();
  const connected = useIsConnected();
  const insets = useSafeAreaInsets();

  const {loading, error} = useFetchedContent(id as string, 'EXERCISE');

  const exercise = rootStore?.exercise?.getExercise(id);


  const labels = rootStore?.language?.translate?.MOBILE?.STEP_ACTION;

  const [modalVisible, setModalVisible] = React.useState(false);

  const goToHome = () => {

    router.replace(concatPath([dashboardKey ], 1) as string);
  };



  const onPressRetake = () => {
    if (exercise.myExerciceId) {
      rootStore?.exercise.undiscardExercise(exercise._id);
    }
    logAmplitude('Exercise Undiscarded', {
      contentId: id,
      contentName: exercise.title,
    });

  };

  const onPressClose = async () => {
    if (router.canGoBack()){
      router.back();
    } else {
      goToHome();
    }
  };

  if(error) {
    return <Error network={!connected} labels={rootStore?.language.translate.MOBILE?.ERROR} />;
  }

  if (loading) {
    return <Loading withLabel={true} label={labels?.LOADING ? labels?.LOADING : 'Loading data..'} />;
  }

  // getExercise() can return null once loading is done -- a cold store, a stale id,
  // or a fetch that resolved without populating it. The render below reads
  // exercise.completed / .doneToday unguarded, so without this the whole screen
  // threw "Cannot read property 'completed' of null" instead of showing anything.
  if (!exercise) {
    return <Error network={!connected} labels={rootStore?.language.translate.MOBILE?.ERROR} />;
  }

  const SIZE_CLOSE = SPACING['sp-48'];
  return (
    <>
      <ModalChallenge modalVisible={modalVisible} setModalVisible={setModalVisible} exerciseId={id as string} />
      <View style={{ flex: 1, backgroundColor: '#fff', padding: SPACING['sp-16'] , paddingTop: insets.top +SPACING['sp-32']}}>
        <ScrollView  contentContainerStyle={[ {  rowGap: SPACING['sp-32'] }]}>

          <EvolunoClose size={SIZE_CLOSE} onPress={onPressClose} />
          <Show when={exercise?.completed}>
            <Banner type="success" label={labels?.AMAZING_JOB} />
          </Show>
          <Show when={!exercise.completed && exercise.doneToday}>
            <Banner type="info" label={labels?.FANTASTIC_WORK} />
          </Show>

          <Title2>
            {exercise?.title}
          </Title2>
          <Body1Reg
          >
            {exercise?.longDescription}
          </Body1Reg>
        </ScrollView>
        <View style={{alignContent:'center', paddingBottom: SPACING['sp-32'], marginTop: SPACING['sp-24']}}>
          <DesignSystemTwoButtonsVertical
            disallowBelow={exercise.completed}
            disallowTop={exercise.doneToday}
            onPressTop={() => {
              rootStore?.exercise?.setDoneToday(exercise._id);
            }}

            onPressBelow= {() => {

              setModalVisible(true);
            }}

            labelTop={labels?.BUTTON_TOP}

            labelBelow={labels?.BUTTON_BOTTOM}
          />
          <Show when={exercise.completed}>
            <DesignSystemButtonTertiaryButton
              label={labels?.RETAKE}
              onPress={onPressRetake}
            />
          </Show>
        </View>
      </View>
    </>
  );
});

export default StepAction;
